/**
 * Money as people read and type it: "150,000", never "150000".
 *
 * Without a thousands separator an extra zero goes unnoticed: production
 * holds a 15,000 fee marked 150,000 paid. So every amount the app shows groups
 * its thousands with a comma, and every amount field groups them while it is
 * typed (components/MoneyInput.jsx). Digits stay Latin in every language, so
 * a figure reads the same on an Arabic, French or English screen.
 *
 * Kept free of React so the typing rules are unit-tested on their own.
 */

// Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) digits, as typed on an
// Arabic keyboard or pasted from a spreadsheet.
const DIGITS = { '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9' };

export const toLatinDigits = (s) => String(s).replace(/[٠-٩۰-۹]/g, (d) => DIGITS[d]);

/** "150000" → "150,000", "1500.5" → "1,500.5". A trailing "." is kept, so a
 * field can show the decimal mark the person has just typed. */
export const groupThousands = (text) => {
  const s = String(text ?? '');
  const dot = s.indexOf('.');
  const int = dot === -1 ? s : s.slice(0, dot);
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dot === -1 ? grouped : `${grouped}${s.slice(dot)}`;
};

/** An amount to show, with the currency's number of decimals, or with the
 * decimals it has (up to 3, as toLocaleString() did) when none are given.
 * Not a number reads 0, as the currency formatter always has. */
export const formatNumber = (value, decimals) => {
  const n = Number(value);
  const v = Number.isFinite(n) ? n : 0;
  const fixed = decimals === undefined ? v.toFixed(3).replace(/\.?0+$/, '') : v.toFixed(decimals);
  return groupThousands(fixed);
};

/** How many decimals a stored value already has ("15000.5" → 1). */
export const decimalsOf = (value) => {
  const s = String(value ?? '');
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
};

// 999,999,999,999 is far above any fee or salary; past it a number would
// also stop printing as plain digits.
export const MAX_INT_DIGITS = 12;

/**
 * One edit of an amount field. `text` and `caret` are what the input holds
 * after the keystroke, `previous` the raw value before it. Returns the raw
 * value to store ("150000", "12.5" or ""), the grouped text to show and where
 * the caret goes, so it stays right after the digit just typed.
 *
 *  - Arabic-Indic digits count as digits. Anything else that is not a digit or
 *    the decimal mark is dropped, the comma included: the field writes its own
 *    commas, so a typed one is a separator, never a decimal mark ("1,500" is
 *    1500, not 1.5).
 *  - "." (or the Arabic "٫") is the decimal mark, once, and only when the
 *    currency has decimals: in FCFA "150.000" types 150000, never 150.
 *  - At most `decimals` digits after it, MAX_INT_DIGITS before it, and no
 *    leading zeros ("0.5" keeps its zero, ".5" gains one).
 *  - Backspace on a comma deletes the digit before it, Delete the one after,
 *    instead of leaving the caret stuck on a separator that comes back.
 */
export const editAmount = ({ text, caret, decimals = 0, previous = '', inputType = '' }) => {
  const src = toLatinDigits(text ?? '');
  const at = caret === null || caret === undefined ? src.length : Math.min(Math.max(caret, 0), src.length);
  let raw = '';
  let before = 0; // raw characters that came from the text left of the caret
  let ints = 0;
  let decs = 0;
  let dot = false;
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i] === '٫' ? '.' : src[i];
    let keep = false;
    if (c >= '0' && c <= '9') {
      if (dot) {
        if (decs < decimals) { decs += 1; keep = true; }
      } else if (ints < MAX_INT_DIGITS) {
        ints += 1;
        keep = true;
      }
    } else if (c === '.' && decimals > 0 && !dot) {
      dot = true;
      keep = true;
    }
    if (keep) {
      raw += c;
      if (i < at) before += 1;
    }
  }

  const prev = String(previous ?? '');
  if (raw === prev && src.length < groupThousands(prev).length) {
    // Nothing but one of the field's own commas was removed.
    if (inputType === 'deleteContentBackward' && before > 0) {
      raw = raw.slice(0, before - 1) + raw.slice(before);
      before -= 1;
    } else if (inputType === 'deleteContentForward' && before < raw.length) {
      raw = raw.slice(0, before) + raw.slice(before + 1);
    }
  }

  const lead = /^0+(?=\d)/.exec(raw);
  if (lead) {
    raw = raw.slice(lead[0].length);
    before = Math.max(0, before - lead[0].length);
  }
  if (raw.startsWith('.')) {
    raw = `0${raw}`;
    if (before > 0) before += 1;
  }

  const display = groupThousands(raw);
  let pos = 0;
  for (let seen = 0; pos < display.length && seen < before; pos += 1) {
    if (display[pos] !== ',') seen += 1;
  }
  return { raw, display, caret: pos };
};

/**
 * Excel shows amounts grouped too: a number format on the amount columns of
 * an exported sheet, so a cell reads 150,000 and stays a number that sums
 * and re-imports. `columns` are 0-based; only numeric cells change.
 */
export const formatMoneyColumns = (XLSX, ws, columns) => {
  if (!ws || !ws['!ref']) return ws;
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let r = range.s.r; r <= range.e.r; r += 1) {
    for (const c of columns) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 'n') cell.z = Number.isInteger(cell.v) ? '#,##0' : '#,##0.00';
    }
  }
  return ws;
};
